import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@/lib/api';
import { setStoredUser, getPendingPlan, getPendingCycle } from '@/lib/auth';
import { VALID_PLAN_IDS } from '@/components/PricingSection';
import { Loader2 } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          navigate('/login');
          return;
        }

        // Get the role that was set before OAuth
        const pendingRole = localStorage.getItem('pending_role') || 'toc';
        localStorage.removeItem('pending_role');

        // Get a plan chosen on the public pricing page before OAuth, if any
        const pendingPlan = getPendingPlan();
        const pendingCycle = getPendingCycle();

        // Process the session with backend
        const response = await auth.processGoogleSession(sessionId, pendingRole);
        const { user } = response.data;

        setStoredUser(user);

        // Clear the hash and redirect
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate to the billing page with the chosen plan pre-selected when
        // an employer picked one on the public pricing page; otherwise the dashboard.
        if (VALID_PLAN_IDS.includes(pendingPlan) && user.role === 'employer') {
          navigate(
            `/employer/billing?plan=${pendingPlan}` +
              (pendingCycle === 'annual' ? '&cycle=annual' : ''),
            { state: { user }, replace: true }
          );
          return;
        }
        const redirectPath = user.role === 'employer' ? '/employer' : '/toc';
        navigate(redirectPath, { state: { user }, replace: true });
      } catch (error) {
        navigate('/login');
      }
    };

    processSession();
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
}
